import { useEffect, useRef } from 'react';

// The band is rotated. Moving DOM text inside it lands on a different pixel grid
// every frame, so the glyphs seem to pulse. Instead the text is drawn once into a
// bitmap tile, and that tile scrolls in whole device pixels inside a canvas that
// never moves itself — the rotated canvas keeps a constant sampling phase.
//
// Where the browser allows it, the scrolling runs in a worker on an OffscreenCanvas,
// so the band adds no work to the main thread while the page scrolls. Fonts and text
// measuring stay on the main thread; the worker only receives the finished bitmap.
const SPEED = 60; // CSS px per second

// Runs on the main thread and, as source text, inside the worker: it must not use
// imports, closures over module scope or syntax that Babel turns into helper calls
// (destructuring, spread, classes, for...of).
function createScroller(ctx, requestFrame, cancelFrame) {
  var tile = null;
  var background = '#000';
  var speed = 0; // device px per second
  var offset = 0; // device px into the tile
  var last = 0;
  var frame = 0;
  var playing = false;

  function draw() {
    var canvas = ctx.canvas;
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (!tile) return;
    for (var x = -Math.round(offset); x < canvas.width; x += tile.width) {
      ctx.drawImage(tile, x, 0);
    }
  }

  function tick(time) {
    frame = 0;
    if (last && tile) offset = (offset + ((time - last) / 1000) * speed) % tile.width;
    last = time;
    draw();
    if (playing) frame = requestFrame(tick);
  }

  return {
    setTile: function (nextTile, width, height, color, pxPerSecond) {
      if (tile && tile.close) tile.close();
      tile = nextTile;
      if (ctx.canvas.width !== width) ctx.canvas.width = width;
      if (ctx.canvas.height !== height) ctx.canvas.height = height;
      background = color;
      speed = pxPerSecond;
      offset %= tile.width;
      draw();
    },
    setPlaying: function (next) {
      if (next === playing) return;
      playing = next;
      last = 0;
      if (playing) {
        frame = requestFrame(tick);
      } else if (frame) {
        cancelFrame(frame);
        frame = 0;
      }
    },
    dispose: function () {
      this.setPlaying(false);
      if (tile && tile.close) tile.close();
      tile = null;
    },
  };
}

// The worker first answers whether it has requestAnimationFrame; without it the band
// would step on timers, so the main thread keeps the canvas instead.
const WORKER_SOURCE = `
var createScroller = ${createScroller.toString()};
var scroller = null;
self.onmessage = function (event) {
  var data = event.data;
  if (data.probe) {
    self.postMessage({ probe: typeof self.requestAnimationFrame === 'function' });
    return;
  }
  if (data.canvas) {
    scroller = createScroller(
      data.canvas.getContext('2d', { alpha: false }),
      self.requestAnimationFrame.bind(self),
      self.cancelAnimationFrame.bind(self)
    );
  }
  if (!scroller) return;
  if (data.tile) scroller.setTile(data.tile, data.width, data.height, data.background, data.speed);
  if ('playing' in data) scroller.setPlaying(data.playing);
};
`;

const canUseWorker = (canvas) =>
  typeof Worker === 'function' &&
  typeof canvas.transferControlToOffscreen === 'function' &&
  typeof createImageBitmap === 'function' &&
  typeof URL.createObjectURL === 'function';

// Resolves to a renderer that draws either in a worker or on the main thread
const createRenderer = (canvas) => {
  const mainThread = () => {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return null;
    const scroller = createScroller(ctx, window.requestAnimationFrame.bind(window), window.cancelAnimationFrame.bind(window));
    return {
      setTile: (tile, width, height, background, speed) => scroller.setTile(tile, width, height, background, speed),
      setPlaying: (playing) => scroller.setPlaying(playing),
      dispose: () => scroller.dispose(),
    };
  };

  if (!canUseWorker(canvas)) return Promise.resolve(mainThread());

  let worker;
  let url;
  try {
    url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
    worker = new Worker(url);
  } catch {
    if (url) URL.revokeObjectURL(url);
    return Promise.resolve(mainThread());
  }

  return new Promise((resolve) => {
    const fallBack = () => {
      worker.terminate();
      URL.revokeObjectURL(url);
      resolve(mainThread());
    };
    const timer = setTimeout(fallBack, 2000);
    worker.onerror = () => {
      clearTimeout(timer);
      fallBack();
    };
    worker.onmessage = (event) => {
      clearTimeout(timer);
      worker.onerror = null;
      worker.onmessage = null;
      if (!event.data.probe) {
        fallBack();
        return;
      }
      const offscreen = canvas.transferControlToOffscreen();
      worker.postMessage({ canvas: offscreen }, [offscreen]);
      let generation = 0;
      resolve({
        setTile: (tile, width, height, background, speed) => {
          const current = ++generation;
          createImageBitmap(tile).then(
            (bitmap) => {
              if (current !== generation) {
                bitmap.close();
                return;
              }
              worker.postMessage({ tile: bitmap, width, height, background, speed }, [bitmap]);
            },
            () => undefined
          );
        },
        setPlaying: (playing) => worker.postMessage({ playing }),
        dispose: () => {
          generation += 1;
          worker.terminate();
          URL.revokeObjectURL(url);
        },
      });
    };
    worker.postMessage({ probe: true });
  });
};

const Ticker = ({ deliveryLabel, serviceLabel, amount, language }) => {
  const bandRef = useRef(null);
  const trackRef = useRef(null);
  const apiRef = useRef(null);

  // Set up once per mount. The canvas is created here rather than rendered, because an
  // OffscreenCanvas can be handed to a worker only once and StrictMode mounts twice.
  useEffect(() => {
    const band = bandRef.current;
    const track = trackRef.current;
    if (!band || !track) return undefined;

    const canvas = document.createElement('canvas');
    canvas.className = 'ticker__canvas';
    track.appendChild(canvas);

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let renderer = null;
    let content = null;
    let builtKey = '';
    let isVisible = true;
    let isHovered = false;
    let disposed = false;

    const updatePlaying = () => {
      renderer?.setPlaying(isVisible && !isHovered && !document.hidden && !reduceMotion?.matches);
    };

    // force: the text or the loaded fonts changed. Without it the tile is only redrawn when
    // the band's size, font or pixel density actually changed.
    const build = (force) => {
      if (!renderer || !content) return;
      const styles = getComputedStyle(track);
      const fontSize = parseFloat(styles.fontSize);
      const family = styles.fontFamily;
      const dpr = window.devicePixelRatio || 1;

      // Integer CSS size and a backing store of exactly size × dpr keep texels 1:1
      const cssWidth = Math.floor(band.clientWidth);
      const cssHeight = Math.ceil(fontSize * 1.5);
      const key = `${cssWidth}|${cssHeight}|${dpr}|${family}`;
      if (!force && key === builtKey) return;
      builtKey = key;

      track.style.height = `${cssHeight}px`;
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      const width = Math.round(cssWidth * dpr);
      const height = Math.round(cssHeight * dpr);

      const star = getComputedStyle(document.documentElement).getPropertyValue('--saffron').trim() || '#f4b63f';
      const runs = content.labels.flatMap((label) => [
        { text: label, weight: 700, color: '#fff' },
        { text: content.amount, weight: 800, color: '#fff' },
        { text: '✦', weight: 700, color: star },
      ]);

      const gap = fontSize * 0.75;
      const measure = document.createElement('canvas').getContext('2d');
      measure.font = `700 ${fontSize}px ${family}`;
      const capHeight = measure.measureText('H').actualBoundingBoxAscent || fontSize * 0.72;
      const widths = runs.map((run) => {
        measure.font = `${run.weight} ${fontSize}px ${family}`;
        return measure.measureText(run.text).width;
      });
      const period = widths.reduce((sum, runWidth) => sum + runWidth + gap, 0);

      // The canvas is opaque, so the band colour is painted into it instead of blended
      const tile = document.createElement('canvas');
      tile.width = Math.ceil(period * dpr);
      tile.height = height;
      const tileCtx = tile.getContext('2d');
      tileCtx.scale(dpr, dpr);
      tileCtx.textBaseline = 'alphabetic';
      const baseline = (cssHeight + capHeight) / 2;
      let x = 0;
      runs.forEach((run, index) => {
        tileCtx.font = `${run.weight} ${fontSize}px ${family}`;
        tileCtx.fillStyle = run.color;
        tileCtx.fillText(run.text, x, baseline);
        x += widths[index] + gap;
      });

      renderer.setTile(tile, width, height, getComputedStyle(band).backgroundColor, SPEED * dpr);
    };

    apiRef.current = {
      setContent: (next) => {
        content = next;
        build(true);
        // Draw again once the display font (and its Cyrillic subset for RU) has arrived
        if (document.fonts?.load) {
          const { fontFamily } = getComputedStyle(track);
          const sample = `${next.labels.join(' ')} ${next.amount} ✦`;
          Promise.all([700, 800].map((weight) => document.fonts.load(`${weight} 16px ${fontFamily}`, sample)))
            .catch(() => undefined)
            .then(() => !disposed && content === next && build(true));
        }
      },
    };

    createRenderer(canvas).then((created) => {
      if (disposed) {
        created?.dispose();
        return;
      }
      renderer = created;
      build(true);
      updatePlaying();
    });

    const onPointerEnter = (event) => {
      if (event.pointerType !== 'mouse') return;
      isHovered = true;
      updatePlaying();
    };
    const onPointerLeave = () => {
      isHovered = false;
      updatePlaying();
    };

    band.addEventListener('pointerenter', onPointerEnter);
    band.addEventListener('pointerleave', onPointerLeave);
    document.addEventListener('visibilitychange', updatePlaying);
    reduceMotion?.addEventListener?.('change', updatePlaying);

    // No window resize listener: mobile browsers fire it whenever the address bar slides
    // during a scroll. The band's own size and the pixel density are what matter.
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(() => build(false)) : null;
    resizeObserver?.observe(band);

    let densityQuery = null;
    const watchDensity = () => {
      densityQuery?.removeEventListener?.('change', onDensityChange);
      densityQuery = window.matchMedia?.(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      densityQuery?.addEventListener?.('change', onDensityChange);
    };
    function onDensityChange() {
      watchDensity();
      build(false);
    }
    watchDensity();

    const intersectionObserver =
      'IntersectionObserver' in window
        ? new IntersectionObserver(([entry]) => {
            isVisible = entry.isIntersecting;
            updatePlaying();
          })
        : null;
    intersectionObserver?.observe(band);

    return () => {
      disposed = true;
      apiRef.current = null;
      renderer?.dispose();
      band.removeEventListener('pointerenter', onPointerEnter);
      band.removeEventListener('pointerleave', onPointerLeave);
      document.removeEventListener('visibilitychange', updatePlaying);
      reduceMotion?.removeEventListener?.('change', updatePlaying);
      densityQuery?.removeEventListener?.('change', onDensityChange);
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      canvas.remove();
      track.style.height = '';
    };
  }, []);

  useEffect(() => {
    const locale = language.toLowerCase();
    apiRef.current?.setContent({
      labels: [deliveryLabel, serviceLabel].map((label) => label.toLocaleUpperCase(locale)),
      amount,
    });
  }, [deliveryLabel, serviceLabel, amount, language]);

  return (
    <div ref={bandRef} className="ticker" aria-hidden="true">
      <div ref={trackRef} className="ticker__track" />
    </div>
  );
};

export default Ticker;
